import { Collection, getFirestore } from 'firestorter';
import format from 'string-template';

import { firestorePaths, pathPlaceholder } from '../../utils/paths';
import BaseDocument from '../../Model/BaseDocument';
import CollectionName from '../../utils/collections';

/**
 * The status of the report.
 */
const ReportStatus = {
  OPEN: 'OPEN',
  IGNORED: 'IGNORED',
  COMMENT_REMOVED: 'COMMENT_REMOVED',
};

/**
 * Class representing an Announcement Comment.
 * A comment is a post that is shared to an announcement.
 *
 * @class AnnouncementComments
 * @extends BaseDocument
 */
class AnnouncementCommentReport extends BaseDocument {
  constructor(id, announcementId, announcementCommentId, opts) {
    super(
      `${format(firestorePaths.ANNOUNCEMENT_COMMENT_REPORT, {
        [pathPlaceholder.ANNOUNCEMENT_COMMENT_ID]: announcementCommentId,
        [pathPlaceholder.ANNOUNCEMENT_ID]: announcementId,
      })}/${id}`,
      opts,
    );
  }

  /**
   * The reason for the report.
   * @returns {string}
   */
  get reason() {
    return this.data.reason;
  }

  /**
   * The name of the user who reported the comment.
   * @returns {string}
   */
  get reportedByUsername() {
    return this.data.reportedByUsername;
  }

  /**
   * The name of the user who's comment got reported.
   * @returns {Date}
   */
  get reportedUsername() {
    return this.data.reportedUsername;
  }

  /**
   * The date the report was created.
   * @returns {Date}
   */
  get reportedAt() {
    return this.data.reportedAt?.toDate();
  }

  /**
   * The status of the report.
   * @returns {string}
   */
  get status() {
    return this.data.status;
  }

  /**
   * The ID of the announcement that the reported comment belongs to.
   * @returns {string}
   */
  get announcementId() {
    return this.data.announcementId;
  }

  /**
   * The ID of the comment that was reported.
   * @returns {string}
   */
  get commentId() {
    return this.data.commentId;
  }

  /**
   * Update the status of the report.
   * @param {string} status - The new status of the report.
   * @param {string} updatedBy - The ID of the user who updated the status of the report.
   * @returns {Promise<void>}
   */
  async updateStatus(status, resolvedBy) {
    return this.update({ status, resolvedBy });
  }

  /**
   * Get the announcement comment reports of a coach.
   * @param {string} coachId - The ID of the coach.
   * @param {boolean} showAllReports - Whether to show all reports or just open ones.
   * @returns {Promise<Collection>}
   */
  static async getAnnouncementCommentReportsOfCoach(coachId, showAllReports = false) {
    const db = getFirestore();
    const reportsCollection = new Collection(() => db.collectionGroup(CollectionName.ANNOUNCEMENT_COMMENT_REPORT), {
      createDocument: ({ id }, opts) => {
        // Extract announcementId and commentId from the document reference path
        const docRef = opts?.snapshot?.ref;
        const commentId = docRef?.parent?.parent?.id;
        const announcementId = docRef?.parent?.parent?.parent?.parent?.id;
        return new AnnouncementCommentReport(id, announcementId, commentId, opts);
      },
      query: (ref) => {
        let query = ref.where('coachId', '==', coachId);
        if (!showAllReports) {
          query = query.where('status', '==', 'OPEN');
        }
        return query;
      },
    });
    // Fetch initial data
    await reportsCollection.fetch();
    return reportsCollection;
  }
}

export default AnnouncementCommentReport;
export { ReportStatus };
